/**
 * EntreprisePortal — tuile portail « Entreprise » (Batch #14.243 / #14.315).
 * En-tête : chiffres de la tenue de livres (grand livre reporté) + trois graphiques (dépenses · revenus · global).
 * Sections selon les fonctions ouvertes par le gestionnaire (portal_scope) :
 * Dépenses (reçu photo) · Kilométrage · Factures · Mon entreprise (NEQ, taxes…).
 * Aucun rapport de taxes côté client.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Building2, Car, FileText, Loader2, Receipt } from "lucide-react";
import { API, portalHeaders } from "../helpers.js";
import ExpenseSection from "./ExpenseSection";
import MileageSection from "./MileageSection";
import RevenueSection from "./RevenueSection";
import CompanySection from "./CompanySection";
import EntrepriseKpis from "./EntrepriseKpis";
import EntrepriseCharts from "./EntrepriseCharts";

const SECTION_META = {
  expenses: { label: "Dépenses", icon: Receipt },
  mileage: { label: "Kilométrage", icon: Car },
  revenues: { label: "Factures", icon: FileText },
  company: { label: "Mon entreprise", icon: Building2 },
};
const YEARS = [0, -1, -2].map((d) => new Date().getFullYear() + d);

export default function EntreprisePortal({ primaryColor }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [section, setSection] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/entrepreneur/summary?year=${year}`, { headers: portalHeaders() });
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de charger votre espace entreprise");
    }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-sm text-red-700" data-testid="ent-portal-error">{error}</div>;
  if (!data) return <div className="py-16 text-center" data-testid="ent-portal-loading"><Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto" /></div>;

  const tabs = [...["expenses", "mileage", "revenues"].filter((s) => data.sections.includes(s)), "company"];
  const active = tabs.includes(section) ? section : tabs[0];

  return (
    <div className="space-y-4" data-testid="portal-entreprise">
      <div className="bg-white border rounded-2xl p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-gray-900" data-testid="ent-portal-company-title">{data.company.business_name || "Mon entreprise"}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {data.company.neq ? `NEQ ${data.company.neq}` : "Complétez vos informations d'entreprise dans « Mon entreprise »."}
            </p>
          </div>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="ent-portal-year">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <EntrepriseKpis data={data} year={year} />
      </div>

      <EntrepriseCharts books={data.books} sections={data.sections} />

      <div className="flex gap-2 overflow-x-auto scroll-x-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="ent-portal-tabs">
        {tabs.map((s) => {
          const m = SECTION_META[s];
          const on = s === active;
          return (
            <button key={s} type="button" onClick={() => setSection(s)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium border transition whitespace-nowrap flex-shrink-0 ${on ? "text-white shadow" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              style={on ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
              data-testid={`ent-portal-tab-${s}`}>
              <m.icon className="w-4 h-4" /> {m.label}
            </button>
          );
        })}
      </div>

      {active === "expenses" && <ExpenseSection data={data} primaryColor={primaryColor} onChanged={load} />}
      {active === "mileage" && <MileageSection data={data} primaryColor={primaryColor} onChanged={load} />}
      {active === "revenues" && <RevenueSection data={data} primaryColor={primaryColor} onChanged={load} />}
      {active === "company" && <CompanySection key={JSON.stringify(data.company)} company={data.company} primaryColor={primaryColor} onChanged={load} />}
    </div>
  );
}
